import { HoverableLinkSizes } from '@paywithextend/components'
import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import ArrowLeft from 'ext-ui/svg/icon/ArrowLeft'
import React, {
  ChangeEvent,
  FormEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectLookupTopLevelErrorFromState } from 'src/app/Signin/redux/signin.selectors'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import PasswordInputField from 'src/components/PasswordInputField/PasswordInputField'
import { useUser } from 'src/context/UserProvider/useUser'
import { useDebounceValue } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { resetPasswordUrl } from 'src/navigation/urls'
import { createSetEmail } from 'src/redux/actions/auth.actions'
import {
  selectEmailFromState,
  selectMfaChallengeInitiated,
} from 'src/redux/selectors/auth.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { useNormalizeBackendError } from 'src/utils/error.utils'
import { EMAIL_REGEX } from 'src/utils/validation/string.validation'

import style from './SignInFormV2.style'

export type SignInFormFields = {
  email: string
  password?: string
}

export type SignInFormProps = {
  className?: string
  /** Callback to update parent's password state for MFA verification */
  onPasswordChange?: (password: string) => void
}
/**
 * Sign in form that handles both password and SSO authentication
 * @param props
 * @returns
 */
export default function SignInFormV2(props: SignInFormProps) {
  const { className = '', onPasswordChange } = props

  // Read initial email from Redux (persists across navigation)
  const initialEmail = useSelector(selectEmailFromState)

  // State hooks
  const [formState, setFormState] = useState<SignInFormFields>({
    email: initialEmail ?? '',
    password: '',
  })
  const [errorState, setErrorState] = useState<
    Partial<Record<keyof SignInFormFields, string>>
  >({})
  const [signInState, setSignInState] = useState<'init' | 'password' | 'sso'>(
    'init',
  )
  const [loading, setLoading] = useState<boolean>(false)
  const [topLevelError, setTopLevelError] = useState<undefined | string>()
  const passwordInputRef = useRef<HTMLInputElement>(null)

  // Custom hooks
  const userContext = useUser()
  const translate = useTranslate()
  const dispatch = useDispatch()
  const normalizeBackendError = useNormalizeBackendError()

  // Selector hooks
  const mfaChallenge = useSelector(selectMfaChallengeInitiated)
  const appName = useSelector(selectAppName)
  const topErrorFromRedux = useSelector(selectLookupTopLevelErrorFromState)

  // Top level error handling from Redux here
  useEffect(() => {
    if (topErrorFromRedux) {
      setLoading(false)
    }
    setTopLevelError(topErrorFromRedux)
  }, [topErrorFromRedux])

  // Stop loading when MFA challenge is initiated (SigninPage will show MFA form)
  useEffect(() => {
    if (mfaChallenge) {
      setLoading(false)
    }
  }, [mfaChallenge])

  // Autofocus password field when it appears after email submission
  useEffect(() => {
    if (signInState === 'password') {
      requestAnimationFrame(() => {
        passwordInputRef.current?.focus()
      })
    }
  }, [signInState])

  // Debounce email to Redux so it persists across navigation (e.g., to Reset Password page)
  const debouncedEmail = useDebounceValue(formState.email, 500)
  useEffect(() => {
    dispatch(createSetEmail(debouncedEmail))
  }, [debouncedEmail, dispatch])

  const handleFormChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newState = {
      ...formState,
    }

    newState[event.currentTarget.name as keyof SignInFormFields] =
      event.currentTarget.value

    setFormState(newState)
  }

  const handleResetEmailClick = () => {
    setFormState({
      ...formState,
      password: '',
    })
    setSignInState('init')
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    // Reset error state
    setErrorState({})
    setTopLevelError(undefined)
    setLoading(true)

    if (signInState === 'init') {
      const regexInstance = new RegExp(EMAIL_REGEX)
      // TODO: sso - can simplify checks if we move to useFormFields
      if (
        formState.email.length === 0 ||
        !regexInstance.test(formState.email)
      ) {
        setErrorState({
          email: translate('Please enter a valid email address'),
        })
        setLoading(false)
        return
      }

      try {
        // Make API call to check for auth type
        const authConfig = await userContext.getAuthConfigFromEmail(
          formState.email,
        )
        // Don't do anything if the auth type returned from userProvider
        // is SSO. UserProvider should handle everything on its own from
        // this point onward
        if (authConfig.authType === 'sso') {
          return
        }
        setSignInState(authConfig.authType)
        setLoading(false)
      } catch (error) {
        // Reset loading state on error to prevent infinite spinner
        setLoading(false)

        // Default fallback message
        const defaultErrorMessage = translate(
          'Sorry, something went wrong. Please try again later.',
        )

        // Check for message field which some endpoints return instead of error
        const axiosError = error as {
          response?: { data?: { message?: string } }
        }
        if (axiosError?.response?.data?.message) {
          setTopLevelError(axiosError.response.data.message)
          return
        }

        // Use centralized error normalization for standard error formats
        const normalized = normalizeBackendError(error)
        setTopLevelError(normalized.error || defaultErrorMessage)
      }
      return
    }

    // User has entered their username and password
    if (signInState === 'password') {
      const errorState: Partial<Record<keyof typeof formState, string>> = {}

      if (formState.email.length === 0) {
        errorState['email'] = 'This value cannot be empty'
      }
      if (formState.password?.length === 0 || !formState.password) {
        errorState['password'] = 'This value cannot be empty'
      }

      if (Object.keys(errorState).length > 0) {
        setErrorState(errorState)
        setLoading(false)
        return
      }
      // Make the API call to log a user in
      userContext.loginUserWithPassword(formState.email, formState.password)
    }
  }

  return (
    <div className={cn('SignInForm', 'flex-column', className)} css={style}>
      <div className="flex-column gap-10">
        <div className="flex-column gap-6">
          <div className={'font-h2'}>
            {translate('Welcome to ${appName}', { appName })}
          </div>
          <p>
            {translate(
              'To log into an existing Extend profile, enter your email below. New to Extend?',
            )}{' '}
            <HoverableLink
              className="signup-link"
              to="/signup"
              ariaLabel={translate('Sign Up')}
              size={HoverableLinkSizes.Medium}
              underline
            >
              {translate('Sign Up')}
            </HoverableLink>
          </p>
        </div>
        <form className="flex-column gap-8" onSubmit={handleSubmit}>
          <div className="flex-column gap-2">
            <TextInput
              label={translate('Email')}
              placeholder={translate('Enter your email address')}
              name="email"
              autoComplete="email"
              disabled={!!(formState.email && signInState !== 'init')}
              value={formState.email}
              onChange={handleFormChange}
              errorText={errorState['email']}
            />
          </div>

          {signInState === 'password' && (
            <PasswordInputField
              ref={passwordInputRef}
              id={'login'}
              data-testid="password-input"
              value={formState['password']}
              onChange={(val: string): void => {
                setFormState({
                  ...formState,
                  password: val,
                })
                onPasswordChange?.(val)
              }}
              errors={
                errorState['password'] ? [errorState['password']] : undefined
              }
            />
          )}
          {topLevelError && (
            <span className="errorText" data-testid="signInError">
              {topLevelError}
            </span>
          )}
          <Button
            type="submit"
            loading={loading}
            testId={signInState === 'init' ? 'continue-button' : 'login-button'}
          >
            {translate(signInState === 'init' ? 'Continue' : 'Log in')}
          </Button>
          {signInState === 'password' && (
            <div className="flex-row justify-between">
              <div>
                <Button
                  className="reset-email-button"
                  variant="text"
                  onClick={handleResetEmailClick}
                >
                  <ArrowLeft />
                  <span className="reset-email-button-text">
                    {translate('Use different email')}
                  </span>
                </Button>
              </div>
              <HoverableLink
                className="forget-password-link"
                underline
                to={resetPasswordUrl()}
                href={resetPasswordUrl()}
                size={HoverableLinkSizes.Medium}
                ariaLabelSource={translate('Reset Password')}
              >
                {translate('Reset Password')}
              </HoverableLink>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}
